import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Icono } from '@xiltrion/ui/icono';
import { Spinner } from '@xiltrion/ui/spinner';
import { Button } from '@xiltrion/ui/button';
import { Input } from '@xiltrion/ui/input';
import { Label } from '@xiltrion/ui/label';
import { Slider } from '@xiltrion/ui/slider';
import { Switch } from '@xiltrion/ui/switch';
import { SelectorSimple } from '@xiltrion/ui/bloques/selectores';
import type { AssignmentPolicy } from '@pqrs/contracts';
import { apiPut } from '@/api/client';
import { PageHeader, Card, LoadingState, ErrorState, PRIORITY_LABEL } from '@/components/ui';
import { useAssignmentPolicy, qk } from './api';
import { Field, Help, JsonEditor, notify, notifyError } from './shared';

const WEIGHTS: Array<{ key: keyof AssignmentPolicy['weights']; label: string; hint: string }> = [
  { key: 'specialty', label: 'Especialidad', hint: 'Prefiere a quien tiene la especialidad del caso (una superior cuenta la mitad).' },
  { key: 'availableCapacity', label: 'Capacidad disponible', hint: 'Prefiere a quien tiene más puntos libres.' },
  { key: 'relativeLoad', label: 'Carga relativa', hint: 'Penaliza a quien está proporcionalmente más cargado.' },
  { key: 'recentAssignmentPenalty', label: 'Penalización por asignación reciente', hint: 'Evita darle dos casos seguidos a la misma persona.' },
];

/** Assignment Engine policy. Weights explain "¿Por qué?" in the assignment panel. */
export function AssignmentPage() {
  const qc = useQueryClient();
  const q = useAssignmentPolicy();
  const [p, setP] = useState<AssignmentPolicy | null>(null);
  useEffect(() => { if (q.data) { const { version: _v, ...rest } = q.data; setP(rest); } }, [q.data]);
  const save = useMutation({ mutationFn: (v: AssignmentPolicy) => apiPut('/assignments/policy', v), onSuccess: () => { qc.invalidateQueries({ queryKey: qk.assignmentPolicy }); notify('success', 'Política de asignación guardada'); }, onError: notifyError });
  const total = p ? Object.values(p.weights).reduce((a, b) => a + b, 0) : 0;
  return (
    <div>
      <PageHeader title="Motor de asignación" subtitle="Cómo elige el sistema a la persona responsable. Primero aplica reglas duras (rol, área, disponibilidad, capacidad) y luego puntúa a los candidatos con estos pesos."
        actions={<Button disabled={!p || save.isPending} onClick={() => p && save.mutate(p)}>{save.isPending ? <Spinner /> : <Icono nombre='mdi:check' />} Guardar</Button>} />
      {q.isLoading || !p ? <LoadingState /> : q.isError ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card title="Pesos de puntuación">
            <Help>Los pesos son relativos entre sí: lo importante es la proporción. Suma actual: {total}.</Help>
            <div className="flex flex-col gap-4 mt-3">
              {WEIGHTS.map((w) => (
                <div key={w.key}>
                  <div className="flex items-center justify-between"><Label htmlFor={`w-${w.key}`}>{w.label}</Label><span className="text-sm tnum w-16 text-right">{p.weights[w.key]} <span className="text-muted-foreground">({total ? Math.round((p.weights[w.key] / total) * 100) : 0}%)</span></span></div>
                  <Slider id={`w-${w.key}`} value={[p.weights[w.key]]} min={0} max={100} aria-label={w.label} onValueChange={([v]) => setP({ ...p, weights: { ...p.weights, [w.key]: v ?? 0 } })} className="my-2" />
                  <div className="text-xs text-muted-foreground">{w.hint}</div>
                </div>
              ))}
            </div>
          </Card>
          <div className="flex flex-col gap-4">
            <Card title="Puntos de carga por prioridad">
              <Help>Cada caso consume puntos de la capacidad del funcionario según su prioridad.</Help>
              <div className="grid grid-cols-2 gap-3 mt-3">
                {(['LOW', 'NORMAL', 'HIGH', 'CRITICAL'] as const).map((k) => (
                  <Field key={k} label={PRIORITY_LABEL[k] ?? k} htmlFor={`wp-${k}`}><Input id={`wp-${k}`} type="number" inputMode="numeric" value={p.workloadPoints[k]} min={0} max={100} onChange={(e) => setP({ ...p, workloadPoints: { ...p.workloadPoints, [k]: e.target.value === '' ? 0 : Number(e.target.value) } })} /></Field>
                ))}
              </div>
            </Card>
            <Card title="Reglas">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2"><Switch id="ap-over" checked={p.allowOverCapacity} onCheckedChange={(v) => setP({ ...p, allowOverCapacity: !!v })} /><Label htmlFor="ap-over" className="font-normal">Permitir superar la capacidad cuando nadie tiene cupo</Label></div>
                <Field label="Desempate" htmlFor="ap-tie" hint="Qué hacer cuando dos candidatos obtienen la misma puntuación."><SelectorSimple id="ap-tie" value={p.tieBreaker} options={[{ value: 'LEAST_LOAD', label: 'Menor carga actual' }, { value: 'ROUND_ROBIN', label: 'Turnos (round robin)' }, { value: 'RANDOM', label: 'Aleatorio' }]} onChange={(v) => v && setP({ ...p, tieBreaker: v as AssignmentPolicy['tieBreaker'] })} /></Field>
              </div>
            </Card>
            <Card title="Política como JSON"><JsonEditor id="ap-json" value={p} onChange={(v) => setP(v as AssignmentPolicy)} rows={10} /></Card>
          </div>
        </div>
      )}
    </div>
  );
}
